import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormInput,
  FormPrefixedInput,
  FormSelect,
  DialogFooter,
  Button,
  useMultiStepForm,
} from "web-design-system";
import {
  useInvestorContactInfo,
  useCreateInvestorContactInfo,
  useUpdateInvestorContactInfo,
  useUpdateClient,
} from "creso-core/hooks";
import { ContactDetailsSchema } from "creso-core/zod";
import { BELONGS_TO } from "creso-core/constants";
import { InfoIcon } from "lucide-react";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";

export function ContactDetails() {
  const { client } = useClientOnboarding();
  const { nextStep, previousStep } = useMultiStepForm();

  const { data: details } = useInvestorContactInfo(
    client?.investment_account ?? ""
  );

  const { mutateAsync: updateClient } = useUpdateClient(client?.id ?? 0);

  const { mutateAsync: createContactInfo } = useCreateInvestorContactInfo();

  const { mutateAsync: updateContactInfo } = useUpdateInvestorContactInfo();

  const form = useForm({
    resolver: zodResolver(ContactDetailsSchema),
    values: {
      number: ((details?.number || client?.phone_number) ?? "").replace(
        "+91",
        ""
      ),
      phone_belongs_to: details?.phone_belongs_to ?? ("self" as const),
      email: (details?.email || client?.email) ?? "",
      email_belongs_to: details?.email_belongs_to ?? ("self" as const),
      isd: "+91",
    },
  });

  const onSubmit = async (data: z.infer<typeof ContactDetailsSchema>) => {
    try {
      const payload = {
        ...data,
        investment_account_guid: client?.investment_account ?? "",
        autofilled: false,
        autofill_source: null,
      };
      if (details) {
        await updateContactInfo(payload);
      } else {
        await createContactInfo(payload);
      }
      await updateClient({
        email: data.email,
      });
      nextStep();
    } catch (error) {
      handleError(error, "Failed to update contact information");
    }
  };
  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Contact Details</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="number"
            render={({ field }) => (
              <FormPrefixedInput
                prefix=""
                label="Mobile number"
                disabled
                type="tel"
                {...field}
              >
                +91
              </FormPrefixedInput>
            )}
          />
          <FormField
            control={form.control}
            name="phone_belongs_to"
            render={({ field }) => (
              <FormSelect
                options={BELONGS_TO}
                label="Mobile belongs to"
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => <FormInput label="Email" {...field} />}
          />
          <FormField
            control={form.control}
            name="email_belongs_to"
            render={({ field }) => (
              <FormSelect
                options={BELONGS_TO}
                label="Email belongs to"
                {...field}
              />
            )}
          />
          <DialogFooter className="mt-auto flex sm:flex-col gap-4">
            <div className="flex items-start p-3 border gap-2 border-border rounded-lg bg-tag-slate text-tag-slate-foreground">
              <InfoIcon className="size-4 stroke-tag-slate-foreground" />
              <span className="text-xs">
                Mark as self only if email and phone belongs to the client
              </span>
            </div>
            <div className="flex items-center w-full gap-5">
              <Button variant="outline" type="button" onClick={previousStep}>
                Previous
              </Button>
              <Button
                className="flex-1"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                Next
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
